// WorkspaceAdminService.js

// Firebase
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

const LIVE_ROOMS_COLLECTION = 'live_rooms';
const LIVE_USAGE_MONTHLY_COLLECTION = 'live_usage_monthly';

/**
 * toCurrentUtcMonthKey
 *
 * Builds a UTC month key in YYYY-MM format.
 *
 * @returns {string} Current UTC month key.
 */
function toCurrentUtcMonthKey() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * normalizeNonNegativeInteger
 *
 * Parses a non-negative integer with fallback.
 *
 * @param {unknown} value - Raw numeric value.
 * @param {number} fallback - Fallback integer.
 *
 * @returns {number} Parsed non-negative integer.
 */
function normalizeNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.floor(parsed);
  if (whole < 0) return fallback;
  return whole;
}

/**
 * normalizeWorkspaceRow
 *
 * Normalizes an admin workspace row from Firestore.
 *
 * @param {object} docSnap - Firestore document snapshot.
 *
 * @returns {object} Normalized workspace row.
 */
function normalizeWorkspaceRow(docSnap) {
  const row = docSnap?.data() || {};
  const key = String(row.key || docSnap.id || '').trim() || docSnap.id;
  const rawMemberCount = Number(row.memberCount);
  const memberCount = Number.isFinite(rawMemberCount) && rawMemberCount >= 0
    ? Math.floor(rawMemberCount)
    : 0;

  return {
    ...row,
    id: docSnap.id,
    key,
    memberCount,
    ownerKey: String(row.ownerKey || row.userKey || '').trim(),
    planKey: String(row.planKey || '').trim(),
    planTitle: String(row.planTitle || '').trim(),
    title: String(row.title || 'Untitled Workspace').trim() || 'Untitled Workspace',
  };
}

/**
 * Lists all workspaces for the admin console.
 *
 * @returns {Promise<Array<object>>} Workspace rows sorted by title then key.
 */
export async function listAdminWorkspaces() {
  const snapshot = await getDocs(collection(db, collections.workspaces));

  return snapshot.docs
    .map(normalizeWorkspaceRow)
    .sort((left, right) => {
      const titleSort = left.title.localeCompare(right.title);
      if (titleSort !== 0) return titleSort;
      return left.key.localeCompare(right.key);
    });
}

/**
 * Updates a workspace plan assignment.
 *
 * @param {object} input - Update input.
 * @param {string} input.workspaceKey - Workspace key.
 * @param {string} input.planKey - Plan key or empty string to clear.
 * @param {string} input.planTitle - Plan title or empty string to clear.
 * @param {string} input.assignedBy - User key of the admin making assignment.
 *
 * @returns {Promise<void>}
 */
export async function updateWorkspacePlanAssignment({
  workspaceKey,
  planKey,
  planTitle,
  assignedBy,
}) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();

  if (!cleanedWorkspaceKey) {
    throw new Error('workspaceKey is required');
  }

  const cleanedPlanKey = String(planKey || '').trim();
  const cleanedPlanTitle = String(planTitle || '').trim();
  const cleanedAssignedBy = String(assignedBy || '').trim();

  if (cleanedPlanKey && !cleanedPlanTitle) {
    throw new Error('planTitle is required when planKey is set');
  }

  await updateDoc(doc(db, collections.workspaces, cleanedWorkspaceKey), {
    dateModified: serverTimestamp(),
    planAssignedAt: serverTimestamp(),
    planAssignedBy: cleanedAssignedBy || null,
    planKey: cleanedPlanKey || null,
    planTitle: cleanedPlanKey ? cleanedPlanTitle : null,
  });
}

/**
 * Reads current-month live usage and active room stats for a workspace.
 *
 * @param {object} input - Read input.
 * @param {string} input.workspaceKey - Workspace key.
 * @param {string} [input.monthKey] - Optional month key override (YYYY-MM).
 *
 * @returns {Promise<object>} Live usage snapshot.
 */
export async function getWorkspaceLiveUsageSnapshot({
  workspaceKey,
  monthKey,
}) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();
  if (!cleanedWorkspaceKey) {
    throw new Error('workspaceKey is required');
  }

  const resolvedMonthKey = String(monthKey || '').trim() || toCurrentUtcMonthKey();
  const workspaceRef = doc(db, collections.workspaces, cleanedWorkspaceKey);
  const usageRef = doc(workspaceRef, LIVE_USAGE_MONTHLY_COLLECTION, resolvedMonthKey);

  const activeRoomsQuery = query(
    collection(workspaceRef, LIVE_ROOMS_COLLECTION),
    where('isActive', '==', true)
  );

  const [usageSnap, activeRoomsSnap] = await Promise.all([
    getDoc(usageRef),
    getDocs(activeRoomsQuery),
  ]);

  const usageRow = usageSnap.exists() ? (usageSnap.data() || {}) : {};
  const activeRooms = activeRoomsSnap.docs.map((roomSnap) => roomSnap.data() || {});

  const activeRoomCount = activeRooms.length;
  const activeParticipantCount = activeRooms.reduce((total, roomRow) => {
    return total + normalizeNonNegativeInteger(roomRow?.participantCount, 0);
  }, 0);

  return {
    activeParticipantCount,
    activeRoomCount,
    dateModified: usageRow?.dateModified || null,
    egressSeconds: normalizeNonNegativeInteger(usageRow?.egressSeconds, 0),
    monthKey: resolvedMonthKey,
    participantSeconds: normalizeNonNegativeInteger(usageRow?.participantSeconds, 0),
  };
}
